import { Divider, Drawer, Grid, Typography, Paper } from '@material-ui/core';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import { RegistroResponse, RegistroStatus } from 'common/genericTypes';
import { Attachment } from 'components/uploadDocs';
import { Button, SmSpace, TextField } from 'm2m.components';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { documentShortDescriptions } from 'common/genericDescriptions';

export enum ValidationRequestKind {
  INSTRUMENTO_FINANCEIRO = 'INSTRUMENTO_FINANCEIRO',
  PARTICIPANTE = 'PARTICIPANTE',
  CREDENCIADO = 'CREDENCIADO',
}

interface ValidationDrawerProps {
  ticket?: string;
  opened: boolean;
  onClose: () => void;
  reject?: boolean;
  onSubmit: (status: RegistroStatus, justification?: string, files?: File[]) => void;
  kind: ValidationRequestKind;
  disableButtons?: boolean;
  registroDetails?: RegistroResponse;
  rejectionMessage?: string | undefined;
  existingAttachments?: Attachment[];
}

export const ValidationDrawer: React.FC<ValidationDrawerProps> = (props) => {
  const [justification, setJustification] = useState<string>();
  const [attachments, setAttachments] = useState<Attachment[]>();
  const dispatch = useDispatch();
  const instrumentoApi = useSelector((state: RootState) => state.instrumentoApi);

  const descriptions = {
    [ValidationRequestKind.INSTRUMENTO_FINANCEIRO]: 'do instrumento financeiro',
    [ValidationRequestKind.PARTICIPANTE]: 'do participante',
    [ValidationRequestKind.CREDENCIADO]: 'do credenciado',
  };

  const handleAutomaticJustification = () => {
    setJustification(props.rejectionMessage);
  };

  const handleSubmitClick = (status: RegistroStatus) => () => {
    props.onSubmit(
      status,
      justification,
      attachments?.map((attachment) => attachment.file!)
    );
    setJustification(undefined);
    setAttachments(undefined);
  };


const handleDownloadAttachment = async (att: Attachment) => {
  try {
    const docId = att?.remoteFileInfo?.id;
    if (!docId) return;

    const signedUrl = await instrumentoApi.getInstrumentoDocumentDownloadUrl(dispatch as any, docId, true);
    if (!signedUrl) return;

    window.open(signedUrl, '_blank', 'noopener,noreferrer');
  } catch (error) {
    console.error('Error downloading attachment:', error);
  }
};

  useEffect(() => {
    handleAutomaticJustification();
  }, [props.rejectionMessage]);

  return (
    <Drawer
      anchor="right"
      open={props.opened}
      onClose={props.onClose}
      PaperProps={{ style: { padding: '40px', width: 520 } }}
    >
      <Grid container>
        <Grid item sm={12}>
          <Typography variant="h3">{`${props.reject ? 'Rejeitar' : 'Aprovar'} Cadastro`}</Typography>
          <SmSpace />
          <Divider variant="fullWidth" />
          <SmSpace />
          {props.registroDetails?.solicitanteId && (
            <>
              <Typography variant="body2">{`Solicitante: ${props.registroDetails?.solicitanteName}`}</Typography>
              <Typography variant="body2">
                {`Participante: ${props.registroDetails?.solicitanteParticipanteName}`}
              </Typography>
              <SmSpace />
            </>
          )}
          {!props.reject && (
            <>
              <Typography variant="body2" display={'inline'}>
                {`Tem certeza que deseja aprovar o cadastro ${descriptions[props.kind]}`}
              </Typography>
              &nbsp;<Typography display={'inline'}>{props.ticket}</Typography>&nbsp;
              <Typography display={'inline'}>{'?'}</Typography>
              <SmSpace />
              {props.existingAttachments && props.existingAttachments.length > 0 && (
                <>
                  <SmSpace />
                  <Paper variant="outlined" elevation={0} style={{ borderRadius: 8, overflow: 'hidden' }}>
                    <div
                      style={{
                        background: '#f7f7f7',
                        borderBottom: '1px solid #e0e0e0',
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                        {'Documento(s) do cadastro'}
                      </Typography>
                    </div>
                    {props.existingAttachments.map((att, idx) => {
                      const emissionType = (att.remoteFileInfo as any)?.emissionType as string | undefined;
                      const tipo = emissionType && documentShortDescriptions[emissionType]
                        ? documentShortDescriptions[emissionType]
                        : undefined;
                      return (
                        <div
                          key={`${att.id}-${att.remoteFileInfo?.id}`}
                          style={{
                            padding: 12,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 6,
                            borderTop: idx === 0 ? 'none' : '1px solid #e0e0e0',
                          }}
                          onMouseEnter={(e) => ((e.currentTarget.parentElement as HTMLElement).style.boxShadow = '0 1px 6px rgba(0,0,0,0.08)')}
                          onMouseLeave={(e) => ((e.currentTarget.parentElement as HTMLElement).style.boxShadow = 'none')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <InsertDriveFileOutlinedIcon style={{ color: '#616161' }} fontSize="small" />
                            <Typography
                              variant="body2"
                              color="primary"
                              style={{ textDecoration: 'underline', cursor: 'pointer' }}
                              onClick={() => handleDownloadAttachment(att)}
                            >
                              {att.remoteFileInfo?.fileName || att.id}
                            </Typography>
                          </div>
                          {tipo && (
                            <Typography variant="caption" color="textSecondary">{tipo}</Typography>
                          )}
                        </div>
                      );
                    })}
                  </Paper>
                  <SmSpace />
                </>
              )}
            </>
          )}
          <TextField
            label="Justificativa"
            value={justification}
            rows={5}
            onChange={setJustification}
            multiline
            fullWidth
          />
          <SmSpace />
          <Button
            variant="contained"
            fullWidth
            color={props.reject ? 'error' : 'primary'}
            onClick={handleSubmitClick(props.reject ? RegistroStatus.Reprovado : RegistroStatus.Aprovado)}
            disabled={props.disableButtons ?? false}
          >
            {props.reject ? 'Rejeitar' : 'Aprovar'}
          </Button>
          {props.reject && props.kind !== ValidationRequestKind.PARTICIPANTE && (
            <>
              <SmSpace />
              <Button
                variant="outlined"
                fullWidth
                onClick={handleSubmitClick(RegistroStatus.AguardandoCorrecao)}
                disabled={props.disableButtons ?? false}
              >
                {'Rejeitar com pendências'}
              </Button>
            </>
          )}
          <SmSpace />
          <Button variant="outlined" fullWidth onClick={props.onClose} disabled={props.disableButtons ?? false}>
            {'Cancelar'}
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  );
};
